import i18next from 'i18next';
import { localize } from '@/utils/approval/localize';
import { buildResignationSubject } from '@/utils/approval/resignation';
import {
  buildSalesBrandPermissionSubject,
  isSalesBrandPermissionType,
} from '@/utils/approval/salesBrandPermission';
import { buildNewCorporateCardSubject } from '@/utils/approval/newCorporateCard';
import {
  buildCorporateSealSubject,
  createCorporateSealState,
  isCorporateSealType,
} from '@/utils/approval/corporateSeal';
import {
  buildCorporateCardRequestSubject,
  isCorporateCardRequestType,
} from '@/utils/approval/corporateCardRequest';
import {
  buildAnnualLeaveRequestSubject,
  createAnnualLeaveRequestState,
  isAnnualLeaveRequestType,
} from '@/utils/approval/annualLeaveRequest';
import {
  buildLongServiceLeaveSubject,
  DEFAULT_SERVICE_YEARS,
  isLongServiceLeaveType,
} from '@/utils/approval/longServiceLeave';
import {
  buildMortgageFeeSubject,
  isMortgageFeeType,
} from '@/utils/approval/mortgageFee';
import {
  buildFncoPriceSupplySubject,
  isFncoPriceType,
} from '@/utils/approval/fncoPriceSupply';
import {
  buildExpenseSubject,
  DEFAULT_COST_TYPE,
  isExpensePreApprovalType,
} from '@/utils/approval/expensePreApproval';

export type HireKind = 'new' | 'extension';

const HIRE_KIND_LABEL: Record<HireKind, string> = {
  new: '신규',
  extension: '연장',
};

const SUBJECT_LANGUAGES = ['ko', 'en', 'zh'];

const hireKindLabel = (kind: HireKind, lng?: string): string => {
  const label = HIRE_KIND_LABEL[kind];
  const t = lng ? i18next.getFixedT(lng) : i18next.t.bind(i18next);
  const out = t(label, { context: 'hire' });
  return typeof out === 'string' && out ? out : label;
};

/** 작성자 언어로 저장된 제목도 읽도록 세 언어 표기를 모두 봄 */
const hireKindMarks = (kind: HireKind): string[] =>
  Array.from(
    new Set([
      `(${HIRE_KIND_LABEL[kind]})`,
      ...SUBJECT_LANGUAGES.map((lng) => `(${hireKindLabel(kind, lng)})`),
    ]),
  );

const HIRE_SUBJECT_BUILDERS: Readonly<
  Record<number, (kind: string) => string>
> = {
  219: (kind) =>
    localize('아르바이트 채용({{kind}})의 건 (사업부/부서/이름)', { kind }),
  220: (kind) =>
    localize('파견직 채용({{kind}})의 건 (사업부/부서/파견업체/이름)', {
      kind,
    }),
};

/** 신규/연장 여부로 제목을 만드는 채용 품의(219·220) */
export const isHireKindApprovalType = (apvType: number): boolean =>
  apvType in HIRE_SUBJECT_BUILDERS;

export const buildHireSubject = (apvType: number, kind: HireKind): string =>
  HIRE_SUBJECT_BUILDERS[apvType]?.(hireKindLabel(kind)) ?? '';

/**
 * 라디오 재선택 — 제목의 (신규)/(연장) 표시만 바꿔 작성자가 채운 나머지를 보존.
 * 표시가 없으면 양식 기본 문구로 다시 씀
 */
export const switchHireSubject = (
  apvType: number,
  subject: string,
  kind: HireKind,
): string => {
  const to = `(${hireKindLabel(kind)})`;
  if (subject.includes(to)) return subject;
  const from = [
    ...hireKindMarks(kind === 'new' ? 'extension' : 'new'),
    ...hireKindMarks(kind),
  ].find((mark) => subject.includes(mark));
  if (from) return subject.replace(from, to);
  return buildHireSubject(apvType, kind);
};

/** 편집 진입 시 저장 제목으로 신규/연장 복원 — 선택값은 제목에만 남음 */
export const parseHireKind = (subject: string): HireKind =>
  hireKindMarks('extension').some((mark) => subject.includes(mark))
    ? 'extension'
    : 'new';

/** 제목에 작성자 이름이 들어가는 양식 — 이름(passport 응답) 도착 전엔 자동채움을 미뤄야 빈 괄호로 굳지 않음 */
export const needsWriterName = (apvType: number): boolean =>
  apvType === 208 ||
  apvType === 196 ||
  apvType === 204 ||
  isCorporateCardRequestType(apvType) ||
  isAnnualLeaveRequestType(apvType) ||
  isLongServiceLeaveType(apvType) ||
  isSalesBrandPermissionType(apvType);

/**
 * 어드민 기본 제목 대신 작성 화면이 만드는 제목. 해당 없으면 null.
 * 채용 품의는 신규 기준, 공용 법인카드(208)·퇴직원(196)·신규입사자 법인카드(204)는 작성자 이름을 붙임
 */
export const buildFormDefaultSubject = (
  apvType: number,
  writerName: string,
): string | null => {
  if (isHireKindApprovalType(apvType)) return buildHireSubject(apvType, 'new');
  if (isExpensePreApprovalType(apvType)) {
    return buildExpenseSubject(DEFAULT_COST_TYPE);
  }
  if (apvType === 208) {
    return localize('공용 법인카드 사용 신청의 건 ({{name}})', {
      name: writerName,
    });
  }
  if (apvType === 196) return buildResignationSubject(writerName);
  if (apvType === 204) return buildNewCorporateCardSubject(writerName);
  if (isCorporateCardRequestType(apvType)) {
    return buildCorporateCardRequestSubject(apvType, writerName);
  }
  if (isAnnualLeaveRequestType(apvType)) {
    return buildAnnualLeaveRequestSubject(
      createAnnualLeaveRequestState({
        name: writerName,
        employeeNumber: '',
        department: '',
        hireDate: '',
      }),
    );
  }
  if (isLongServiceLeaveType(apvType)) {
    return buildLongServiceLeaveSubject(DEFAULT_SERVICE_YEARS, {
      name: writerName,
      departmentName: '',
    });
  }
  if (isCorporateSealType(apvType)) {
    return buildCorporateSealSubject(createCorporateSealState());
  }
  if (isMortgageFeeType(apvType)) return buildMortgageFeeSubject();
  if (isSalesBrandPermissionType(apvType)) {
    return buildSalesBrandPermissionSubject(writerName);
  }
  if (isFncoPriceType(apvType)) return buildFncoPriceSupplySubject(apvType);
  return null;
};
